import {useCurrentPageType} from '@aboutyou/router';
import {
  COLORS,
  ErrorBoundary,
  Z_INDEXES,
  mediaLargeScreens,
  useIsScrollingUp,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {FC, memo, useMemo, useRef} from 'react';
import {Sticky} from 'react-stickup-ay';
import {TabStorageProvider} from '../../providers/TabStorageProvider';
import {BasketHint} from '../Basket/BasketHint/BasketHint';
import {HeaderTopRowContainer} from './HeaderTopRowContainer';
import {EnableSettingsModalProps} from './IconMetaNavigation/IconMetaNavigationItemUser/FlyoutLoggedIn';
import {MetaBar} from './MetaBar/MetaBar';
import {TopCategoryNavigation} from './TopCategoryNavigation/TopCategoryNavigation';

interface HeaderProps {
  enableSettingsModalComponent: (
    props: EnableSettingsModalProps,
  ) => React.ReactNode;
  voucherToastsShowDirection?: 'up' | 'down' | 'left' | 'right';
}

export const Header: FC<HeaderProps> = memo(function Header({
  enableSettingsModalComponent,
}) {
  const isScrollingUp = useIsScrollingUp();
  const currentRoute = useCurrentPageType();

  const shouldIncreaseStickyOffset =
    isScrollingUp && currentRoute === 'category';

  const notificationBellRef = useRef<HTMLDivElement>(null);

  const basketHint = useMemo(() => {
    return (
      <ErrorBoundary renderContext="basket_hint" ErrorComponent={null}>
        <TabStorageProvider>
          <BasketHint notificationBellRef={notificationBellRef} />
        </TabStorageProvider>
      </ErrorBoundary>
    );
  }, []);

  return (
    <Wrapper data-testid="header">
      <ErrorBoundary renderContext="header_meta_bar">
        <MetaBar />
      </ErrorBoundary>
      <ErrorBoundary renderContext="header_top_row">
        <HeaderTopRowContainer
          enableSettingsModalComponent={enableSettingsModalComponent}
          notificationBellRef={notificationBellRef}
        />
      </ErrorBoundary>
      <Sticky
        style={{zIndex: Z_INDEXES.DESKTOP_HEADER}}
        defaultOffsetTop={shouldIncreaseStickyOffset ? 70 : 25}
      >
        <ShopHintsContainer>{basketHint}</ShopHintsContainer>
      </Sticky>
      <ErrorBoundary renderContext="header_navigation">
        <TopCategoryNavigation />
      </ErrorBoundary>
    </Wrapper>
  );
});

const Wrapper = styled.header`
  position: relative;
  width: 100%;
  z-index: ${Z_INDEXES.DESKTOP_HEADER};
  background-color: ${COLORS.White};
`;

const ShopHintsContainer = styled.div`
  position: absolute;
  width: 100%;
  max-width: 450px;
  top: 100%;
  right: 20px;

  @media ${mediaLargeScreens} {
    right: 60px;
  }
`;
